import type { NavigateFunction } from 'react-router-dom';
import { coachStorageKey, COACH_STORAGE_SUFFIX } from '../coachStorage';

export interface TeamDashboardContext {
  teamId: string;
  teamName: string;
  returnTo: string;
}

export interface TeamDashboardLocationState {
  teamDashboard?: TeamDashboardContext;
}

function teamDashboardStorageKey(userId: string): string {
  return coachStorageKey(userId, COACH_STORAGE_SUFFIX.teamDashboardContext);
}

export function buildTeamDashboardContext(teamId: string, teamName: string): TeamDashboardContext {
  return {
    teamId,
    teamName,
    returnTo: `/coach/teams/${teamId}?tab=sessions`,
  };
}

export function persistTeamDashboardContext(context: TeamDashboardContext, userId: string): void {
  try {
    sessionStorage.setItem(teamDashboardStorageKey(userId), JSON.stringify(context));
  } catch {
    // Ignore quota / private mode errors
  }
}

export function readTeamDashboardContext(userId: string): TeamDashboardContext | null {
  const storageKey = teamDashboardStorageKey(userId);
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TeamDashboardContext;
    if (!parsed?.teamId || !parsed.returnTo) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(storageKey);
    return null;
  }
}

export function clearTeamDashboardContext(userId: string): void {
  sessionStorage.removeItem(teamDashboardStorageKey(userId));
}

export function mergeTeamDashboardFromLocationState(
  state: TeamDashboardLocationState | null | undefined,
  userId: string
): TeamDashboardContext | null {
  if (state?.teamDashboard?.teamId) {
    persistTeamDashboardContext(state.teamDashboard, userId);
    return state.teamDashboard;
  }
  return readTeamDashboardContext(userId);
}

export function isSessionInTeamContext(
  context: TeamDashboardContext | null | undefined,
  teamId: string | null | undefined
): boolean {
  if (!context || !teamId) return false;
  return context.teamId === teamId;
}

export function getActiveTeamDashboardContext(
  context: TeamDashboardContext | null | undefined,
  teamId: string | null | undefined
): TeamDashboardContext | null {
  return isSessionInTeamContext(context, teamId) ? (context ?? null) : null;
}

export function navigateToTeamSession(
  navigate: NavigateFunction,
  sessionId: string,
  context: TeamDashboardContext,
  userId: string
): void {
  persistTeamDashboardContext(context, userId);
  navigate(`/coach/dashboard/${sessionId}`, { state: { teamDashboard: context } });
}
